import { useState, useMemo } from "react";

const useRdCalculator = () => {
    const [monthlyInvestment, setMonthlyInvestment] = useState(5000);
    const [rate, setRate] = useState(7);
    const [years, setYears] = useState(5);

    const { maturityValue, investedAmount, totalInterest } = useMemo(() => {
        if (monthlyInvestment <= 0 || rate <= 0 || years <= 0) {
            return { maturityValue: 0, investedAmount: 0, totalInterest: 0 };
        }

        const quarterlyRate = rate / 100 / 4;
        const totalMonths = years * 12;

        let maturity = 0;
        for (let month = 1; month <= totalMonths; month++) {
            const remainingMonths = totalMonths - month + 1;
            const quarters = remainingMonths / 3;
            maturity += monthlyInvestment * Math.pow(1 + quarterlyRate, quarters);
        }

        const totalInvested = monthlyInvestment * totalMonths;
        const roundedValue = Math.floor(maturity);

        return {
            maturityValue: roundedValue,
            investedAmount: totalInvested,
            totalInterest: roundedValue - totalInvested,
        };
    }, [monthlyInvestment, rate, years]);
    return {
        title: "RD Calculator",

        inputs: [
            {
                label: "Monthly Investment",
                value: monthlyInvestment,
                setter: setMonthlyInvestment,
                min: 500,
                max: 100000,
                step: 500,
            },
            {
                label: "Rate of Interest (p.a)",
                value: rate,
                setter: setRate,
                min: 1,
                max: 15,
                step: 0.1,
            },
            {
                label: "Time Period (Years)",
                value: years,
                setter: setYears,
                min: 1,
                max: 20,
                step: 1,
            },
        ],

        investedAmount,
        estimatedReturns: totalInterest,
        totalValue: maturityValue,

        infoContent: {
            heading: "RD Calculator – Recurring Deposit",
            description:
                "A Recurring Deposit (RD) allows you to invest a fixed amount every month and earn guaranteed returns through compound interest.",

            subHeading1: "What is a RD Calculator?",
            subContent1:
                "An RD calculator estimates the maturity value of your monthly deposits based on interest rate and investment tenure.",

            subHeading2: "How It Works",
            subContent2:
                "It calculates compound interest on monthly contributions over the selected time period.",

            formula:
                "M = P × [((1 + r/n)^(n×t) − 1) / (r/n)]",

            subHeading3: "Benefits",
            benefits: [
                "Encourages disciplined monthly savings",
                "Guaranteed returns",
                "Low-risk investment",
                "Suitable for short-term financial goals",
            ],
        }
    };
};

export default useRdCalculator;
